import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {UntypedFormBuilder} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {concat, forkJoin, Observable, ObservableInput, of, Subject} from 'rxjs';
import {LeftMenuService} from '@services/left-menu/left-menu.service';
import {BucketBrowserPageService} from '@services/left-menu/page/bucket-browser-page/bucket-browser-page.service';
import {UtilsService} from '@services/utils/utils.service';
import {NotificationService} from '@services/notification/notification.service';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {MenuNotificationService} from '@services/menu-notification/menu-notification.service';
import {ConfirmationDialogService} from '@services/confirmation-dialog.service';
import {UserDefinedPageService} from '@services/left-menu/page/user-defined-page/user-defined-page.service';
import {ModalDialogDismissOption} from '@shared/confirmation-dialog/modal-dialog-dismiss-option';
import {PageItem} from '@models/sidebar/page-item';
import {MenuItem} from '@models/sidebar/menu-item';
import {UserMenuItem} from '@models/sidebar/response/user-menu-item';
import {NodeRequest} from '@models/sidebar/request/node-request';
import {MenuItemType} from '@models/sidebar/menu-item-type';
import {PageTypeOption} from '@models/sidebar/response/page-type-option';
import {MenuNotification, OperationType} from '@models/sidebar/menu-notification';

import {
  BUCKET_BROWSER_PAGE,
  EXTERNAL_PAGE,
  FORM_PAGE,
  FRAME_PAGE,
  STATUS_EVENT_GROUP_PAGE,
  STATUS_EVENT_PAGE,
  TABLEAU_PAGE,
  TEMPLATE_STATUS_EVENT_PAGE,
  TEMPLATE_USER_DEFINED_PAGE,
  USER_DEFINED_PAGE
} from '@constants/sidebar/page-type-option-object';
import {StatusEventPageService} from '@services/pages/status-event-page/configuration/status-event-page.service';
import {catchError, switchMap, takeUntil} from 'rxjs/operators';
import {ExternalUrlService} from '@services/left-menu/page/external-url-page/external-url.service';
import {TableauPageService} from '@services/left-menu/page/tableau-page/tableau-page.service';
import {FramePageService} from '@services/left-menu/page/frame-page/frame-page.service';
import {FormPageService} from '@services/left-menu/page/form-page/form-page.service';
import {
  UpdateItemsInsideNodeService
} from '@services/left-menu/update-items-inside-node/update-items-inside-node.service';
import {MESSAGE_TYPE} from '@constants/message-type';
import {SortingService} from '@services/administration/sorting/sorting.service';
import {DeleteObservableService} from '@services/left-menu/delete-observable.service';
import {ToggleFavoriteService} from '@services/left-menu/favorites/toggle-favorite/toggle-favorite.service';
import {
  FavoriteNotificationService
} from '@services/left-menu/favorites/favorite-notification/favorite-notification.service';
import {SelectPageService} from '@services/left-menu/select-page/select-page.service';
import {StorageService} from '@services/storage/storage.service';
import {
  StatusEventGroupPageService
} from '@services/pages/status-event-group-page/configuration/status-event-group-page.service';
import {TemplatePagesService} from '@services/left-menu/page/template-user-defined-page/template-pages.service';
import {UpdateItemsInsideNodeMessage} from '@models/sidebar/update-items-inside-node-message';
import {EnvironmentConfig} from '@environments/environment';

@Component({
    selector: 'scitis-edit-menu-page',
    templateUrl: './edit-menu-page.component.html',
    styleUrls: ['./edit-menu-page.component.scss'],
    standalone: false
})
export class EditMenuPageComponent implements OnInit, OnDestroy {

  @Input()
  menuItem: MenuItem = new MenuItem();

  @Input()
  baseMenuItems: PageItem[] = [];

  @Input()
  userMenuItems: PageItem[] = [];

  @Input()
  templateMenuItems: PageItem[] = [];

  @Input()
  isAdminOrHigher: boolean = false;

  @Input()
  isHigherThanAdmin: boolean = false;

  @Input()
  firebaseUserId: string = '';

  addNewPage: boolean = false;
  addNewNode: boolean = false;
  saveButtonDisabled: boolean = false;

  baseMenuItemsOrderChanged = false;
  userMenuItemsOrderChanged = false;
  pageTypeOptions: PageTypeOption[] = [];
  pageItemsToBeDeleted: PageItem[] = [];
  originalBaseMenuItems: PageItem[] = [];
  originalUserMenuItems: PageItem[] = [];
  itemsInsideNodeMap: Map<string, PageItem[]> = new Map<string, PageItem[]>();
  refreshNodes: string[] = [];
  allPagesInsideNode: PageItem[] = [];
  sharedMenuItems = '';
  nodeFunctionalityEnabled: boolean = EnvironmentConfig.PortalSettings.nodeFunctionalityEnabled || false;
  isMachineGroup: boolean = false;
  isDeletionInProgress: boolean = false;

  private SELECTED_PAGE_KEY = '';
  private errorCreatingNode = '';
  private errorRenamingNode = '';
  private successCreatingNode = '';
  private successRenamingNode = '';
  private errorUpdatingMenuItems = '';
  private savingIndicatorMessage = '';
  private deleteMessageBody = '';
  private deleteMessage = '';
  private partialUpdateMessage = '';
  private menuIsAlreadyUpToDateMessage = '';
  private successSharingUserDefinedPage = '';
  private errorSharingUserDefinedPage = '';
  private successUnsharingUserDefinedPage = '';
  private errorUnsharingUserDefinedPage = '';
  private shareUserDefinedPageDuplicateNameMessageBody = '';
  private shareUserDefinedPageBody = '';
  private shareMessage = '';
  private unshareMessageBody = '';
  private unshareMessage = '';
  private CURRENT_PAGE_NAME = 'EditMenuPage';
  private propertiesFallback: Object = {};
  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
      private activeModal: NgbActiveModal,
      private fb: UntypedFormBuilder,
      private leftMenuService: LeftMenuService,
      private bucketBrowserPageService: BucketBrowserPageService,
      private utilsService: UtilsService,
      private notificationService: NotificationService,
      private translation: TranslateService,
      private loadingIndicator: LoadingIndicatorService,
      private menuNotificationService: MenuNotificationService,
      private confirmationDialogService: ConfirmationDialogService,
      private statusEventPageService: StatusEventPageService,
      private statusEventGroupPageService: StatusEventGroupPageService,
      private userDefinedPageService: UserDefinedPageService,
      private templatePagesService: TemplatePagesService,
      private externalUrlService: ExternalUrlService,
      private tableauPageService: TableauPageService,
      private framePageService: FramePageService,
      private formPageService: FormPageService,
      private updateItemsInsideNodeService: UpdateItemsInsideNodeService,
      private sortingService: SortingService,
      private deleteObservableService: DeleteObservableService,
      private toggleFavoriteService: ToggleFavoriteService,
      private favoriteNotificationService: FavoriteNotificationService,
      private selectPageService: SelectPageService,
      private storageService: StorageService) {
    this.setTranslations();
  }

  get iconClass(): string {
    return this.menuItem.icon.toString();
  }

  get dragAndDropEnabledForBaseMenuItems(): boolean {
    return this.isAdminOrHigher;
  }

  get displayAddPageButton(): boolean {
    if (this.menuItem.isMachine) {
      return true;
    }
    return this.isHigherThanAdmin;
  }

  get addFormIsActive(): boolean {
    return this.addNewNode || this.addNewPage;
  }

  get isMachineTab(): boolean {
    return this.menuItem.type === MenuItemType.MACHINE;
  }

  static changeIndexes(pageItems: PageItem[]): void {
    let index = 0;
    for (const pageItem of pageItems) {
      pageItem.index = index;
      index++;
    }
  }

  ngOnInit() {
    this.determineIfMachineryGroupOrMachine();
    this.backupMenuItems();
    this.setPageTypeOptions();
    this.SELECTED_PAGE_KEY = 'selectedPage' + this.firebaseUserId;
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  onClose(): void {
    this.loadingIndicator.hide();
    this.activeModal.dismiss(ModalDialogDismissOption.CLOSE);
  }

  onCancel(): void {
    this.loadingIndicator.hide();
    this.activeModal.dismiss(ModalDialogDismissOption.CANCEL);
  }

  onAddPage(): void {
    this.addNewPage = true;
    this.addNewNode = false;
  }

  onSaveNewPage(pageItem: PageItem): void {
    this.onDisableSaveButton(false);
    if (this.menuItem.machineId || this.menuItem.id) {
      switch (pageItem.type) {

        case MenuItemType.MACHINE_GROUP_STATUS_PAGE:
          this.statusEventGroupPageService.create(this.baseMenuItems, pageItem, String(this.menuItem.id), this.CURRENT_PAGE_NAME, this.getParentId(), this.isAdminOrHigher);
          break;
        case MenuItemType.BUCKET_BROWSER_PAGE:
          this.bucketBrowserPageService.create(this.baseMenuItems, pageItem, this.menuItem.machineId, this.CURRENT_PAGE_NAME, this.getParentId(), this.isAdminOrHigher);
          break;
        case MenuItemType.STATUS_EVENT_PAGE:
          this.statusEventPageService.create(this.baseMenuItems, pageItem, this.menuItem.machineId, this.CURRENT_PAGE_NAME, this.getParentId(), this.isAdminOrHigher);
          break;
        case MenuItemType.EXTERNAL_URL:
          this.externalUrlService.create(this.baseMenuItems, pageItem, this.getMachineOrGroupId(), this.CURRENT_PAGE_NAME, this.getParentId(), this.isAdminOrHigher, this.isMachineTab);
          break;
        case MenuItemType.FORM_PAGE:
          this.formPageService.create(this.baseMenuItems, pageItem, this.menuItem.machineId, this.CURRENT_PAGE_NAME, this.getParentId(), this.isAdminOrHigher);
          break;
        case MenuItemType.USER_DEFINED_PAGE:
          this.userDefinedPageService.create(this.userMenuItems, pageItem, this.menuItem.machineId, this.CURRENT_PAGE_NAME, this.getParentId(), this.isAdminOrHigher, this.menuItem.isMachine, this.firebaseUserId);
          break;
        case MenuItemType.TABLEAU_PAGE:
          this.tableauPageService.create(this.baseMenuItems, pageItem, this.getMachineOrGroupId(), this.CURRENT_PAGE_NAME, this.getParentId(), this.isAdminOrHigher, this.isMachineTab);
          break;
        case MenuItemType.FRAME_PAGE:
          this.framePageService.create(this.baseMenuItems, pageItem, this.getMachineOrGroupId(), this.CURRENT_PAGE_NAME, this.getParentId(), this.isAdminOrHigher, this.isMachineTab);
          break;
        case MenuItemType.TEMPLATE_USER_DEFINED_PAGE:
          this.templatePagesService.create(this.templateMenuItems, this.baseMenuItems, pageItem, this.menuItem.machineId, this.CURRENT_PAGE_NAME, this.getParentId(), this.isAdminOrHigher, this.menuItem.isMachine, this.firebaseUserId, false);
          break;
        case MenuItemType.TEMPLATE_STATUS_EVENT_PAGE:
          this.templatePagesService.create(this.templateMenuItems, this.baseMenuItems, pageItem, this.menuItem.machineId, this.CURRENT_PAGE_NAME, this.getParentId(), this.isAdminOrHigher, this.menuItem.isMachine, this.firebaseUserId, true);
          break;
      }
    }
    this.addNewPage = false;
  }

  onSave(): void {
    const observables = new Array<Observable<any>>();
    const menuNotifications: MenuNotification[] = [];
    if (this.pageItemsToBeDeleted.length > 0) {
      this.handlePageDeletion(observables, menuNotifications);
    } else {
      this.addItemsOrderChangeObservables(observables, menuNotifications);
      this.updateParentForItemsInsideNodeObservables(observables, menuNotifications);
      this.saveChanges(observables, menuNotifications);
    }
  }

  onCancelSavePage(): void {
    this.addNewPage = false;
    this.onDisableSaveButton(false);
  }

  onDeletePageItem(pageItem: PageItem): void {
    this.pageItemsToBeDeleted = [...this.pageItemsToBeDeleted, pageItem];
    if (pageItem.type) {
      switch (pageItem.type) {
        case MenuItemType.BUCKET_BROWSER_PAGE:
        case MenuItemType.EXTERNAL_URL:
        case MenuItemType.DATA_VISUALIZATION_PAGE:
        case MenuItemType.STATUS_EVENT_PAGE:
        case MenuItemType.MACHINE_GROUP_STATUS_PAGE:
        case MenuItemType.FORM_PAGE:
        case MenuItemType.TABLEAU_PAGE:
        case MenuItemType.FRAME_PAGE:
        case MenuItemType.USER_SHARED_PAGE:
        case MenuItemType.TEMPLATE_USER_DEFINED_PAGE:
        case MenuItemType.TEMPLATE_STATUS_EVENT_PAGE:
          this.baseMenuItems = this.baseMenuItems.filter(m => m.id !== pageItem.id);
          break;
        case MenuItemType.NODE:
        case MenuItemType.USER_DEFINED_PAGE:
          this.userMenuItems = this.userMenuItems.filter(m => m.id !== pageItem.id);
          break;
      }
    }
  }

  setPropertiesFallback(propertiesFallback) {
    this.propertiesFallback = propertiesFallback;
  }

  onEditPageItem(pageItem: PageItem): void {
    if (pageItem.type) {
      switch (pageItem.type) {
        case MenuItemType.BUCKET_BROWSER_PAGE:
          this.bucketBrowserPageService.rename(pageItem, this.menuItem.machineId, this.getParentId(), this.CURRENT_PAGE_NAME, this.propertiesFallback);
          break;
        case MenuItemType.EXTERNAL_URL:
          this.externalUrlService.update(pageItem, this.getMachineOrGroupId(), this.getParentId(), this.CURRENT_PAGE_NAME, this.isMachineTab, this.propertiesFallback);
          break;
        case MenuItemType.STATUS_EVENT_PAGE:
          this.statusEventPageService.rename(pageItem, this.menuItem.machineId, this.getParentId(), this.CURRENT_PAGE_NAME, this.propertiesFallback);
          break;
        case MenuItemType.MACHINE_GROUP_STATUS_PAGE:
          this.statusEventGroupPageService.rename(pageItem, this.menuItem.machineGroupId as number, this.getParentId(), this.CURRENT_PAGE_NAME, this.propertiesFallback);
          break;
        case MenuItemType.FORM_PAGE:
          this.formPageService.rename(pageItem, this.menuItem.machineId, this.getParentId(), this.CURRENT_PAGE_NAME, this.propertiesFallback);
          break;
        case MenuItemType.TABLEAU_PAGE:
          this.tableauPageService.rename(pageItem, this.getMachineOrGroupId(), this.getParentId(), this.CURRENT_PAGE_NAME, this.isMachineTab, this.propertiesFallback);
          break;
        case MenuItemType.FRAME_PAGE:
          this.framePageService.update(pageItem, this.getMachineOrGroupId(), this.getParentId(), this.CURRENT_PAGE_NAME, this.isMachineTab, this.propertiesFallback);
          break;
        case MenuItemType.USER_DEFINED_PAGE:
        case MenuItemType.USER_SHARED_PAGE:
          this.userDefinedPageService.rename(pageItem, this.menuItem.machineId, this.getParentId(), this.CURRENT_PAGE_NAME, this.menuItem.isMachine, this.propertiesFallback);
          break;
        case MenuItemType.TEMPLATE_USER_DEFINED_PAGE:
        case MenuItemType.TEMPLATE_STATUS_EVENT_PAGE:
          this.templatePagesService.rename(pageItem, this.menuItem.machineId, this.CURRENT_PAGE_NAME);
          break;
      }
    }
  }

  onSharePageItem(pageItem: PageItem): void {
    this.confirmSharePage(pageItem, false, undefined);
  }

  onSharePageItemInsideNode(map: Map<string, PageItem>): void {
    const [nodeId] = map.keys();
    const pageInsideNode = map.get(nodeId) as PageItem;
    this.confirmSharePage(pageInsideNode, true, nodeId);
  }

  onGetPagesInsideNode(pagesInsideNode: PageItem[]): void {
    this.allPagesInsideNode = this.allPagesInsideNode.concat(pagesInsideNode);
  }

  onEditPageItemInsideNode(pageItem: PageItem): void {
    this.userDefinedPageService.onEditPageItemInsideNode(pageItem, this.menuItem.machineId, this.getParentId(), this.CURRENT_PAGE_NAME, this.menuItem.isMachine, this.propertiesFallback);
  }

  onUnsharePageItem(pageItem: PageItem): void {
    this.confirmationDialogService.handleConfirmationDialog(this.unshareMessage, this.unshareMessageBody, '', this.unshareMessage)
    .pipe(takeUntil(this.destroy$))
    .subscribe(userConfirmed => {
      if (userConfirmed) {
        this.unshare(pageItem);
      }
    });
  }

  onBaseMenuItemsOrderChanged(reorderedBaseMenuItems: PageItem[]): void {
    this.baseMenuItems = reorderedBaseMenuItems;
    this.baseMenuItemsOrderChanged = true;
  }

  onUserMenuItemsOrderChanged(reorderedBUserMenuItems: PageItem[]): void {
    this.userMenuItems = reorderedBUserMenuItems;
    this.userMenuItemsOrderChanged = true;
  }

  onDisableSaveButton(disable: boolean): void {
    this.saveButtonDisabled = disable;
  }

  onDeactivateAddForms(): void {
    this.addNewPage = false;
    this.addNewNode = false;
  }

  onAddNode(): void {
    this.addNewNode = true;
    this.addNewPage = false;
  }

  onUpdateNode(pageItem: PageItem): void {
    if (this.menuItem.machineId) {
      this.loadingIndicator.show(this.savingIndicatorMessage);
      const userMenuItem: UserMenuItem = new UserMenuItem();
      userMenuItem.id = pageItem.id;
      userMenuItem.index = pageItem.index;
      userMenuItem.machineId = pageItem.machineId;
      userMenuItem.name = pageItem.name;
      userMenuItem.parent = pageItem.parent;
      userMenuItem.type = pageItem.type;
      if (pageItem.firebaseUserId) {
        userMenuItem.firebaseUserId = pageItem.firebaseUserId;
      }
      delete userMenuItem.parentId;
      this.leftMenuService.updateNode(this.menuItem.machineId, userMenuItem)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (node: UserMenuItem) => {
          this.favoriteNotificationService.broadcast(new MenuNotification(OperationType.UPDATE_NAME, userMenuItem.id, '', node.name));
          this.loadingIndicator.hide();
          this.notificationService.success(this.successRenamingNode, this.CURRENT_PAGE_NAME, false);
          this.userMenuItems.map((item) => {
            if (item.id === node.id) {
              item.name = node.name;
            }
          });
        }, error: (error) => {
          this.loadingIndicator.hide();
          this.notificationService.error(this.errorRenamingNode, error, this.CURRENT_PAGE_NAME, true);
        }
      });
    }
  }

  onSaveNode(pageItem: PageItem): void {
    this.onDisableSaveButton(false);
    if (this.menuItem.machineId) {
      this.loadingIndicator.show(this.savingIndicatorMessage);
      const createNodeRequest = new NodeRequest(pageItem);
      this.leftMenuService.createNode(this.menuItem.machineId, createNodeRequest)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (node: UserMenuItem) => {
          this.menuNotificationService.broadcast(new MenuNotification(OperationType.ADD, this.getParentId(), node.name, ''));
          this.addNewNode = false;
          this.userMenuItems.push(this.leftMenuService.getPageItem(node, true));
          this.loadingIndicator.hide();
          this.notificationService.success(this.successCreatingNode, this.CURRENT_PAGE_NAME, false);
        }, error: (error) => {
          this.loadingIndicator.hide();
          this.notificationService.error(this.errorCreatingNode, error, this.CURRENT_PAGE_NAME, true);
        }
      });
    }
  }

  onCancelSaveNode(): void {
    this.addNewNode = false;
    this.onDisableSaveButton(false);
  }

  getSaveButtonDisabled(): boolean {
    return this.addNewNode || this.addNewPage || this.saveButtonDisabled || this.isDeletionInProgress;
  }

  getParentId(): string | null {
    return this.leftMenuService.getParentId(this.menuItem);
  }

  getMachineOrGroupId(): string {
    return this.leftMenuService.getMachineOrGroupId(this.menuItem);
  }

  getOrderedPageItems(pageItems: PageItem[]): PageItem[] {
    EditMenuPageComponent.changeIndexes(pageItems);

    const clonePageItems: PageItem[] = this.utilsService.getObjectDeepCopy(pageItems);
    for (const pageItem of clonePageItems) {
      delete pageItem.canBeAltered;
      delete pageItem.formIsActive;
      delete pageItem.parentId;
      delete pageItem.menuItemParent;
      if (!pageItem.organizationId) {
        delete pageItem.organizationId;
      }
      if (!pageItem.machineGroupId) {
        delete pageItem.machineGroupId;
      }
      if (!pageItem.pageId) {
        delete pageItem.pageId;
      }
    }
    return clonePageItems;
  }

  setPageTypeOptions(): void {
    this.pageTypeOptions = [];

    if (this.menuItem.machineId && this.isAdminOrHigher) {
      this.templatePagesService.isTemplateFunctionalityAvailable(this.menuItem.machineId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (result[0]) {
          this.pageTypeOptions.push(TEMPLATE_USER_DEFINED_PAGE);
          this.pageTypeOptions.push(TEMPLATE_STATUS_EVENT_PAGE);
        }
      });
    }

    if (this.isHigherThanAdmin) {
      if (this.menuItem.machineId) {
        this.pageTypeOptions.push(USER_DEFINED_PAGE);
        this.pageTypeOptions.push(EXTERNAL_PAGE);
        this.pageTypeOptions.push(FORM_PAGE);
        this.pageTypeOptions.push(TABLEAU_PAGE);
        this.pageTypeOptions.push(FRAME_PAGE);
        this.pageTypeOptions.push(BUCKET_BROWSER_PAGE);
        this.pageTypeOptions.push(STATUS_EVENT_PAGE);
      } else {
        this.pageTypeOptions.push(FRAME_PAGE);
        this.pageTypeOptions.push(TABLEAU_PAGE);
        this.pageTypeOptions.push(EXTERNAL_PAGE);
      }
    } else if (this.isAdminOrHigher) {
      if (this.menuItem.machineId) {
        this.pageTypeOptions.push(USER_DEFINED_PAGE);
        this.pageTypeOptions.push(TABLEAU_PAGE);
        this.pageTypeOptions.push(STATUS_EVENT_PAGE);
      } else {
        this.pageTypeOptions.push(TABLEAU_PAGE);
        this.pageTypeOptions.push(STATUS_EVENT_PAGE);
      }
    } else {
      this.pageTypeOptions.push(USER_DEFINED_PAGE);
    }
    this.pageTypeOptions = this.pageTypeOptions.sort((a, b) => this.sortingService.sortBySpecifiedProperty(a, b, 'label'));
  }

  addItemsOrderChangeObservables(observables: Observable<any>[], menuNotifications: MenuNotification[]): void {
    if (this.baseMenuItemsOrderChanged && this.baseMenuItems.length > 0) {
      const orderedPageItems = this.getOrderedPageItems(this.baseMenuItems);
      observables.push(this.leftMenuService.changeBaseMenuItemsOrder(this.menuItem.isMachineGroup, orderedPageItems).pipe(catchError(err => of(err))));
      menuNotifications.push(new MenuNotification(OperationType.UPDATE_BASE_MENU_ITEMS_ORDER, this.getParentId(), '', ''));
    }
    if (this.userMenuItemsOrderChanged && this.userMenuItems.length > 0) {
      const orderedPageItems = this.getOrderedPageItems(this.userMenuItems);
      const parentId = this.getParentId();
      if (parentId && this.menuItem.machineId) {
        this.updateFavoriteParent(this.userMenuItems, parentId, this.menuItem.name, true);
        observables.push(this.leftMenuService.changeUserMenuItemsOrder(this.menuItem.machineId, parentId, this.menuItem.isMachine, orderedPageItems).pipe(catchError(err => of(err))));
        menuNotifications.push(new MenuNotification(OperationType.UPDATE_USER_MENU_ITEMS_ORDER, this.getParentId(), '', ''));
      }
    }
  }

  handlePageDeletion(observables: Observable<any>[], menuNotifications: MenuNotification[]): void {
    const pageNames = this.getPagesToBeDeletedNames();
    const deleteMessageBody = this.deleteMessageBody.replace('%s', pageNames);
    this.confirmationDialogService.handleConfirmationDialog(this.deleteMessage, deleteMessageBody, '', this.deleteMessage)
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: userConfirmed => {
        if (userConfirmed) {
          this.addDeletePageItemsObservables(observables, menuNotifications);
          this.addItemsOrderChangeObservables(observables, menuNotifications);
          this.updateParentForItemsInsideNodeObservables(observables, menuNotifications);
          this.saveDeleteChanges(observables, menuNotifications);
        } else {
          this.restoreToInitialState();
        }
      }, error: () => {
        this.loadingIndicator.hide();
      }
    });
  }

  getPagesToBeDeletedNames(): string {
    let pageNames = '<br><ul>';
    let index = 1;
    const numberOfPages = this.pageItemsToBeDeleted.length;
    for (const pageItem of this.pageItemsToBeDeleted) {
      let pageNameTobeDeleted = pageItem.name.toBold() + ' [' + this.translation.instant(pageItem.type) + ']';
      if (index === numberOfPages) {
        pageNameTobeDeleted += '?';
      }
      pageNames += '<li>' + pageNameTobeDeleted + '</li>';
      index++;
    }
    return pageNames + '</ul>';
  }

  restoreToInitialState(): void {
    this.baseMenuItems = this.originalBaseMenuItems;
    this.userMenuItems = this.originalUserMenuItems;

    this.baseMenuItemsOrderChanged = false;
    this.userMenuItemsOrderChanged = false;

    this.refreshNodes = [];
    for (const page of this.pageItemsToBeDeleted) {
      const isBaseMenuItem = this.baseMenuItems.filter(m => m.id === page.id);
      const isUserMenuItem = this.userMenuItems.filter(m => m.id === page.id);
      if (isBaseMenuItem.length === 0 && isUserMenuItem.length === 0 && page.parentId) {
        this.refreshNodes.push(page.parentId);
      }
    }
    this.pageItemsToBeDeleted = [];
  }

  backupMenuItems(): void {
    this.originalBaseMenuItems = this.baseMenuItems;
    this.originalUserMenuItems = this.userMenuItems;
  }

  onItemsInsideNodeChanged(map: Map<string, PageItem[]>): void {
    this.itemsInsideNodeMap = map;
  }

  updateParentForItemsInsideNodeObservables(observables: Observable<any>[], menuNotifications: MenuNotification[]): void {
    for (const key of this.itemsInsideNodeMap.keys()) {
      const splitKey = key.split('_');
      const parentId: string = splitKey[0];
      const parentName: string = splitKey[1];
      const menuItems = this.itemsInsideNodeMap.get(key);
      if (menuItems && this.menuItem.machineId) {
        this.updateFavoriteParent(menuItems, parentId, parentName, false);
        const orderedPageItems = this.leftMenuService.getPageItems(menuItems);
        observables.push(this.leftMenuService.changeUserMenuItemsOrder(this.menuItem.machineId, parentId, false, orderedPageItems).pipe(catchError(err => of(err))));
        menuNotifications.push(new MenuNotification(OperationType.UPDATE_USER_MENU_ITEMS_ORDER, this.getParentId(), '', ''));
      }
    }
  }

  private handleSharePageInsideNodeResponse(pageInsideNode: PageItem, request: UserMenuItem, sharedPage: any, nodeId: string | undefined): void {
    const parentId = this.getParentId();
    this.menuNotificationService.broadcast(new MenuNotification(OperationType.ADD, parentId, pageInsideNode.name, ''));
    this.favoriteNotificationService.broadcast(new MenuNotification(OperationType.SHARE_OR_UN_SHARE_PAGE, null, '', ''));
    const pageItem = this.leftMenuService.getPageItem(request, this.isAdminOrHigher);
    pageItem.type = MenuItemType.USER_SHARED_PAGE;
    this.setNewPageProperties(pageItem, sharedPage.id);
    this.moveUserSharedPage(pageItem);
    this.updateItemsInsideNodeService.broadcast(new UpdateItemsInsideNodeMessage(nodeId));
    this.loadingIndicator.hide();
    this.notificationService.success(this.successSharingUserDefinedPage, this.CURRENT_PAGE_NAME, false);
  }

  private updateFavoriteParent(menuItems: PageItem[], parentId: string, parentName: string, isParentMachine: boolean) {
    for (const item of menuItems) {
      if (item.id && this.toggleFavoriteService.isFavorite(item.id)) {
        this.toggleFavoriteService.updateFavoriteItemParent(String(item.id), String(parentId), parentName, isParentMachine);
      }
    }
  }

  private saveChanges(observables: Observable<any>[], menuNotifications: MenuNotification[]): void {
    if (observables.length > 0) {
      this.loadingIndicator.show(this.savingIndicatorMessage);
      forkJoin(observables)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: results => {
          this.activeModal.dismiss(ModalDialogDismissOption.OK);
          this.loadingIndicator.hide();
          this.redirectActiveDeletedPage(menuNotifications, this.selectPageService.getSelectedPageId);
          if (menuNotifications?.length) {
            menuNotifications.map((menu, i) => {
              if (menu.operation === OperationType.DELETE_PAGE && results[i] === null) {
                results[i] = {status: 200};
              }
            });
          }
          this.displayNotification(menuNotifications, results);
        }, error: error => {
          console.log('Failed to save changes', error);
          this.notificationService.error(this.errorUpdatingMenuItems, error, this.CURRENT_PAGE_NAME, true);
          this.loadingIndicator.hide();
        }
      });
    } else {
      this.activeModal.dismiss(ModalDialogDismissOption.OK);
      this.notificationService.success(this.menuIsAlreadyUpToDateMessage, this.CURRENT_PAGE_NAME, false);
    }
  }

  private saveDeleteChanges(observables: Observable<any>[], menuNotifications: MenuNotification[]): void {
    if (!observables || observables.length === 0) {
      this.activeModal.dismiss(ModalDialogDismissOption.OK);
      this.notificationService.success(this.menuIsAlreadyUpToDateMessage, this.CURRENT_PAGE_NAME, false);
      return;
    }

    this.loadingIndicator.show(this.savingIndicatorMessage);
    this.isDeletionInProgress = true;
    const concatenatedObservables = concat(...observables);
    concatenatedObservables
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (result) => {
        this.notifyOnPageDeletion(result, menuNotifications);
      },
      error: error => {
        this.notifyOnPageDeletion(error, menuNotifications);
      },
      complete: () => {
        this.activeModal.dismiss(ModalDialogDismissOption.OK);
        this.isDeletionInProgress = false;
        this.loadingIndicator.hide();
        this.redirectActiveDeletedPage(menuNotifications, this.selectPageService.getSelectedPageId);
        const result: any[] = new Array(menuNotifications?.length);
        if (menuNotifications?.length) {
          menuNotifications.map((menu, i) => {
            if (menu.operation === OperationType.DELETE_PAGE) {
              if (!menu.result) {
                result[i] = {status: 200};
              } else {
                result[i] = menu.result;
              }
            }else{
              result[i] = {status: 200};
            }
          });
        }
        this.displayNotification(menuNotifications, result);
      }
    });

  }

  private notifyOnPageDeletion(result: any, menuNotifications: MenuNotification[]): void {
    if (!result) {
      return;
    }
    let isSuccess: boolean;
    let pageId: string = '';
    let statusCode: number;
    if (!result.error) {
      isSuccess = true;
      pageId = result[0];
      statusCode = 200;
    } else {
      isSuccess = false;
      pageId = result.error[0];
      statusCode = result.status;
    }

    if (!pageId) {
      return;
    }

    const menuNotification = menuNotifications.find(p => pageId === String(p.pageId));
    if (!menuNotification) {
      return;
    }
    menuNotification.result = {status: statusCode};
    const pageName = menuNotification.name;
    if (!pageName) {
      return;
    }
    if (isSuccess) {
      const message = this.translation.instant('The page #pageName was deleted').replace('#pageName', pageName.toBold());
      this.notificationService.success(message, this.CURRENT_PAGE_NAME, true);
    } else {
      const message = this.translation.instant('The page #pageName was NOT deleted').replace('#pageName', pageName.toBold());
      this.notificationService.warning(message, this.CURRENT_PAGE_NAME);
    }

  }

  private setNewPageProperties(pageItem: PageItem, id: string | null) {
    pageItem.id = id;
    pageItem.canBeAltered = this.isAdminOrHigher;
  }

  private redirectActiveDeletedPage(menuNotifications: MenuNotification[], activeId: string | null): void {
    for (const menuNotification of menuNotifications) {
      if (menuNotification.operation === OperationType.DELETE_PAGE && String(menuNotification.pageId) === String(activeId)) {
        let commands;
        if (this.baseMenuItems[0].type === MenuItemType.MACHINE) {
          commands = this.leftMenuService.getCommands(this.menuItem, this.firebaseUserId, undefined);
          this.storageService.writeValueToIndexedDb(this.SELECTED_PAGE_KEY, this.menuItem.id);
          this.selectPageService.setSelectedPageId(this.menuItem.id);
        } else {
          commands = this.leftMenuService.getCommands(this.baseMenuItems[0], this.firebaseUserId, undefined);
          this.storageService.writeValueToIndexedDb(this.SELECTED_PAGE_KEY, this.baseMenuItems[0].id);
          this.selectPageService.setSelectedPageId(this.baseMenuItems[0].id);
        }
        this.leftMenuService.navigateToPage(commands);
      }
    }
  }

  private displayNotification(menuNotifications: MenuNotification[], results: any[]): void {
    const notificationMessage = this.leftMenuService.getResultMessage(menuNotifications, results);
    if (notificationMessage?.type) {
      switch (notificationMessage.type) {
        case MESSAGE_TYPE.WARNING:
          this.notificationService.warning(notificationMessage.message, this.CURRENT_PAGE_NAME, this.partialUpdateMessage);
          this.menuNotificationService.broadcast(new MenuNotification(OperationType.UPDATE_BASE_MENU_ITEMS_ORDER, this.getParentId(), '', ''));
          break;
        case MESSAGE_TYPE.SUCCESS:
          this.notificationService.success(notificationMessage.message, this.CURRENT_PAGE_NAME, true);
          this.menuNotificationService.broadcast(new MenuNotification(OperationType.UPDATE_BASE_MENU_ITEMS_ORDER, this.getParentId(), '', ''));
          break;
        case MESSAGE_TYPE.ERROR:
          this.notificationService.error(notificationMessage.message, null, this.CURRENT_PAGE_NAME, true);
          break;
      }
    }
  }

  private addDeletePageItemsObservables(observables: Observable<any>[], menuNotifications: MenuNotification[]): void {
    this.favoriteNotificationService.clearPendingFavoriteNotifications();
    for (const pageItem of this.pageItemsToBeDeleted) {
      if (pageItem.type) {
        const machineOrGroupId: string = this.leftMenuService.getMachineOrGroupId(this.menuItem);
        const isParentMachineGroup = this.menuItem.type === MenuItemType.MACHINE_GROUP;
        delete pageItem.formIsActive;
        switch (pageItem.type) {
          case MenuItemType.BUCKET_BROWSER_PAGE:
            this.baseMenuItemsOrderChanged = true;
            this.deleteObservableService.setBucketBrowserPageDeleteObservable(observables, menuNotifications, pageItem, this.menuItem);
            break;
          case MenuItemType.STATUS_EVENT_PAGE:
            this.baseMenuItemsOrderChanged = true;
            this.deleteObservableService.setStatusEventPageDeleteObservable(observables, menuNotifications, pageItem, this.menuItem);
            break;
          case MenuItemType.MACHINE_GROUP_STATUS_PAGE:
            this.baseMenuItemsOrderChanged = true;
            this.deleteObservableService.setStatusEventGroupPageDeleteObservable(observables, menuNotifications, pageItem, this.menuItem);
            break;
          case MenuItemType.EXTERNAL_URL:
            this.baseMenuItemsOrderChanged = true;
            this.deleteObservableService.setExternalUrlDeleteObservable(observables, menuNotifications, pageItem, machineOrGroupId, isParentMachineGroup, this.menuItem);
            break;
          case MenuItemType.FORM_PAGE:
            this.baseMenuItemsOrderChanged = true;
            this.deleteObservableService.setFormPageDeleteObservable(observables, menuNotifications, pageItem, this.menuItem);
            break;
          case MenuItemType.NODE:
            this.userMenuItemsOrderChanged = true;
            this.deleteObservableService.setNodeDeleteObservable(observables, menuNotifications, pageItem, this.menuItem, this.userMenuItems.length - 1);
            break;
          case MenuItemType.USER_DEFINED_PAGE:
            this.userMenuItemsOrderChanged = true;
            this.deleteObservableService.setUserDefinedPageDeleteObservable(observables, menuNotifications, pageItem, this.menuItem, false);
            break;
          case MenuItemType.USER_SHARED_PAGE:
            this.userMenuItemsOrderChanged = true;
            this.deleteObservableService.setUserDefinedPageDeleteObservable(observables, menuNotifications, pageItem, this.menuItem, true);
            break;
          case MenuItemType.TEMPLATE_USER_DEFINED_PAGE:
          case MenuItemType.TEMPLATE_STATUS_EVENT_PAGE:
            this.userMenuItemsOrderChanged = true;
            this.deleteObservableService.setTemplatePageDeleteObservable(observables, menuNotifications, pageItem, this.menuItem);
            break;
          case MenuItemType.TABLEAU_PAGE:
            this.baseMenuItemsOrderChanged = true;
            this.deleteObservableService.setTableauPageDeleteObservable(observables, menuNotifications, pageItem, machineOrGroupId, isParentMachineGroup, this.menuItem);
            break;
          case MenuItemType.FRAME_PAGE:
            this.baseMenuItemsOrderChanged = true;
            this.deleteObservableService.setFramePageDeleteObservable(observables, menuNotifications, pageItem, machineOrGroupId, isParentMachineGroup, this.menuItem);
            break;
        }
      }
    }
  }

  private setTranslations(): void {
    this.errorCreatingNode = this.translation.instant('Error creating node.');
    this.errorRenamingNode = this.translation.instant('Error renaming node.');
    this.successCreatingNode = this.translation.instant('Node was created.');
    this.successRenamingNode = this.translation.instant('Node was renamed.');

    this.savingIndicatorMessage = this.translation.instant('Saving');
    this.deleteMessage = this.translation.instant('Delete');
    this.deleteMessageBody = this.translation.instant('Do you really want to permanently delete %s');

    this.errorUpdatingMenuItems = this.translation.instant('Error updating menu items.');
    this.partialUpdateMessage = this.translation.instant('Something failed');
    this.menuIsAlreadyUpToDateMessage = this.translation.instant('The menu is already up to date.');
    this.successSharingUserDefinedPage = this.translation.instant('User defined page has been shared.');
    this.errorSharingUserDefinedPage = this.translation.instant('Error sharing user defined page.');
    this.successUnsharingUserDefinedPage = this.translation.instant('Page is not shared anymore and will be visible only to its owner');
    this.errorUnsharingUserDefinedPage = this.translation.instant('Error unsharing page.');
    this.shareUserDefinedPageDuplicateNameMessageBody = this.translation.instant('A machine item with same name already exists. Do you really want to share this page?');
    this.shareUserDefinedPageBody = this.translation.instant('This page will be visible to all users. Do you really want to share this page?');
    this.shareMessage = this.translation.instant('Share');
    this.unshareMessageBody = this.translation.instant('This page will no longer be visible to all users. Do you really want to unshare this page?');
    this.unshareMessage = this.translation.instant('Unshare');
    this.sharedMenuItems = this.translation.instant('Shared Menu Items');
  }

  private handleSharePageOutsideNodeResponse(pageItem: PageItem, parentId: string | null | undefined, sharedPage: any): void {
    if (parentId) {
      this.menuNotificationService.broadcast(new MenuNotification(OperationType.ADD, parentId, pageItem.name, ''));
    }
    pageItem.type = MenuItemType.USER_SHARED_PAGE;
    this.setNewPageProperties(pageItem, sharedPage.id);
    this.moveUserSharedPage(pageItem);
    this.favoriteNotificationService.broadcast(new MenuNotification(OperationType.SHARE_OR_UN_SHARE_PAGE, null, '', ''));
    this.loadingIndicator.hide();
    this.notificationService.success(this.successSharingUserDefinedPage, this.CURRENT_PAGE_NAME, false);
  }

  private moveUserSharedPage(pageItem: PageItem): void {
    const alreadyExistingMenuItem = this.baseMenuItems.filter(m => m.id === pageItem.id);
    if (alreadyExistingMenuItem.length === 0) {
      this.baseMenuItems.push(pageItem);
    }
    this.userMenuItems = this.userMenuItems.filter(m => m.id !== pageItem.id);
  }

  private unshare(pageItem: PageItem): void {
    const parentId = this.getParentId();
    if (this.menuItem.machineId && pageItem.pageId) {
      this.loadingIndicator.show(this.savingIndicatorMessage);
      const request = this.leftMenuService.getUnshareRequest(pageItem);
      this.userDefinedPageService.unshare(this.menuItem.machineId, pageItem.pageId, request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (userDefinedPage: UserMenuItem) => {
          this.menuNotificationService.broadcast(new MenuNotification(OperationType.ADD, parentId, pageItem.name, ''));
          this.favoriteNotificationService.broadcast(new MenuNotification(OperationType.SHARE_OR_UN_SHARE_PAGE, null, '', ''));
          pageItem.type = MenuItemType.USER_DEFINED_PAGE;
          this.setNewPageProperties(pageItem, userDefinedPage.id);
          this.moveUserDefinedPage(pageItem);
          this.loadingIndicator.hide();
          this.notificationService.success(this.successUnsharingUserDefinedPage, this.CURRENT_PAGE_NAME, false);
        }, error: (error) => {
          this.notificationService.error(this.errorUnsharingUserDefinedPage, error, this.CURRENT_PAGE_NAME, true);
          this.loadingIndicator.hide();
        }
      });
    }
  }

  private moveUserDefinedPage(pageItem: PageItem): void {
    if (String(pageItem.pageId).includes(String(this.firebaseUserId))) {
      this.userMenuItems.push(pageItem);
    }
    this.baseMenuItems = this.baseMenuItems.filter(m => m.id !== pageItem.id);
  }

  private confirmSharePage(pageItem: PageItem, sharePageItemInsideNode: boolean, nodeId: string | undefined): void {
    const baseMenuItemWithSameName = this.baseMenuItems.filter(m => m.name === pageItem.name).length > 0;
    let message;
    if (baseMenuItemWithSameName) {
      message = this.shareUserDefinedPageDuplicateNameMessageBody;
    } else {
      message = this.shareUserDefinedPageBody;
    }
    this.confirmationDialogService.handleConfirmationDialog(this.shareMessage, message, '', this.shareMessage)
    .pipe(takeUntil(this.destroy$))
    .subscribe(userConfirmed => {
      if (userConfirmed) {
        this.share(pageItem, sharePageItemInsideNode, baseMenuItemWithSameName, nodeId);
      }
    });
  }

  private share(pageItem: PageItem, sharePageItemInsideNode: boolean, baseMenuItemWithSameName: boolean, nodeId: string | undefined): void {
    if (baseMenuItemWithSameName) {
      this.updateNameAndSharePage(pageItem, sharePageItemInsideNode, nodeId);
    } else {
      this.justSharePage(pageItem, sharePageItemInsideNode, nodeId);
    }
  }

  private justSharePage(pageItem: PageItem, sharePageItemInsideNode: boolean, nodeId: string | undefined): void {
    const pageIsCurrentlyInNode = UserDefinedPageService.pageIsInsideNode(pageItem);
    const parentId = pageIsCurrentlyInNode ? pageItem.parentId : this.getParentId();
    const isParentMachine = pageIsCurrentlyInNode ? false : this.menuItem.isMachine;
    const request = this.leftMenuService.getUserMenuItem(pageItem, true);
    const shareObservable = this.userDefinedPageService.getShareObservable(pageItem, this.menuItem, parentId, isParentMachine, request);
    shareObservable?.pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (sharedPage) => {
        if (sharePageItemInsideNode) {
          this.handleSharePageInsideNodeResponse(pageItem, request, sharedPage, nodeId);
        } else {
          this.handleSharePageOutsideNodeResponse(pageItem, parentId, sharedPage);
        }
      }, error: (error) => {
        this.notificationService.error(this.errorSharingUserDefinedPage, error, this.CURRENT_PAGE_NAME, true);
        this.loadingIndicator.hide();
      }
    });
  }

  private updateNameAndSharePage(pageItem: PageItem, sharePageItemInsideNode: boolean, nodeId: string | undefined): void {
    const originalName = pageItem.name;
    UserDefinedPageService.setNewName(pageItem, this.baseMenuItems);
    const pageIsCurrentlyInNode = UserDefinedPageService.pageIsInsideNode(pageItem);
    const parentId = pageIsCurrentlyInNode ? pageItem.parentId : this.getParentId();
    const isParentMachine = pageIsCurrentlyInNode ? false : this.menuItem.isMachine;
    let request;
    const renameObservable = this.userDefinedPageService.getRenameObservable(pageItem, this.menuItem.machineId, parentId, this.CURRENT_PAGE_NAME, isParentMachine);
    renameObservable?.pipe(
        takeUntil(this.destroy$),
        switchMap((_result) => {
              this.loadingIndicator.show(this.savingIndicatorMessage);
              request = this.leftMenuService.getUserMenuItem(pageItem, true);
              return this.userDefinedPageService.getShareObservable(pageItem, this.menuItem, parentId, isParentMachine, request) as ObservableInput<any>;
            }
        )).subscribe({
      next: (sharedPage) => {
        if (sharePageItemInsideNode) {
          this.handleSharePageInsideNodeResponse(pageItem, request, sharedPage, nodeId);
        } else {
          this.handleSharePageOutsideNodeResponse(pageItem, parentId, sharedPage);
        }
      }, error: (error) => {
        pageItem.name = originalName;
        this.notificationService.error(this.errorSharingUserDefinedPage, error, this.CURRENT_PAGE_NAME, true);
        this.loadingIndicator.hide();
      }
    });
  }

  private determineIfMachineryGroupOrMachine() {
    if (this.menuItem.type === MenuItemType.MACHINE_GROUP) {
      this.isMachineGroup = true;
    }
  }
}
